/**
 * TransactionForm - REDESIGNED WITH WORLD-CLASS UX
 * 
 * Mobile-First Design:
 * - Step-by-step wizard on mobile
 * - All fields in one scrollable view on desktop
 * - Smart field grouping and progressive disclosure
 * - 100% aligned with database schema
 * 
 * UX Principles:
 * - Show what matters most first (type, amount, description)
 * - Group related fields logically
 * - Visual feedback on every action
 * - Smart defaults
 * - Allow quick creation (minimal required fields)
 * - Allow detailed entry (all optional fields accessible)
 */

import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form@7.55.0';
import { zodResolver } from '@hookform/resolvers/zod';
import { transactionCreateSchema, type TransactionCreateData } from '../../../lib/schemas/transaction.schema';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Separator } from '../../../components/ui/separator';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Combobox } from '../../../components/ui/combobox';
import { MultiSelectCombobox } from '../../../components/ui/multi-select-combobox';
import { CurrencySelect } from '../../../components/financial';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { 
  Loader2, 
  Save, 
  X, 
  TrendingUp, 
  TrendingDown, 
  ArrowLeftRight,
  Calendar,
  Tag,
  CreditCard,
  FileText,
  Users,
  MapPin,
  Hash,
  AlertCircle,
  ChevronRight,
  CheckCircle2,
  Repeat,
  Building2
} from 'lucide-react';
import type { CashFlowEntry, Company } from '../../../types/database';
import { useTransactionOptions } from '../hooks/useTransactionOptions';
import { cn } from '../../../components/ui/utils';
import { motion, AnimatePresence } from 'motion/react';
import { notify } from '../../../lib/notifications';

interface TransactionFormRedesignedProps {
  initialData?: Partial<CashFlowEntry>;
  companyId: string | null;
  selectedCompanies?: Company[];
  onSubmit: (data: TransactionCreateData & { company_id: string | null }) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  isMobile?: boolean;
  forceRecurring?: boolean; // Força toggle de recorrência ativo
}

type FormStep = 'basics' | 'categorization' | 'payment' | 'additional' | 'recurrence' | 'review';

interface ComboboxOption {
  value: string;
  label: string;
}

const FREQUENCY_OPTIONS: ComboboxOption[] = [
  { value: 'daily', label: 'Diária' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'yearly', label: 'Anual' },
];

const CLASSIFICATION_OPTIONS: ComboboxOption[] = [
  { value: 'Fixo', label: 'Fixo' },
  { value: 'Variável', label: 'Variável' },
  { value: 'Eventual', label: 'Eventual' },
];

const TAX_CATEGORY_OPTIONS: ComboboxOption[] = [
  { value: 'Dedutível', label: 'Dedutível' },
  { value: 'Não Dedutível', label: 'Não Dedutível' },
  { value: 'Isento', label: 'Isento' },
];

const BUDGET_CATEGORY_OPTIONS: ComboboxOption[] = [
  { value: 'Receitas Operacionais', label: 'Receitas Operacionais' },
  { value: 'Custos dos Produtos / Serviços', label: 'Custos dos Produtos / Serviços' },
  { value: 'OPEX (Despesas Operacionais)', label: 'OPEX (Despesas Operacionais)' },
  { value: 'Impostos pagos', label: 'Impostos pagos' },
  { value: 'CAPEX / Compra de Ativos', label: 'CAPEX / Compra de Ativos' },
  { value: 'Venda de Ativos', label: 'Venda de Ativos' },
  { value: 'Outras Receitas não operacionais', label: 'Outras Receitas não operacionais' },
  { value: 'Capital / Aportes de Sócios', label: 'Capital / Aportes de Sócios' },
  { value: 'Empréstimos recebidos', label: 'Empréstimos recebidos' },
  { value: 'Amortização de empréstimos', label: 'Amortização de empréstimos' },
  { value: 'Distribuição de Lucros / Dividendos / JCP', label: 'Distribuição de Lucros / Dividendos / JCP' },
  { value: 'Adiantamentos a Sócios', label: 'Adiantamentos a Sócios' },
  { value: 'Devolução de Adiantamentos', label: 'Devolução de Adiantamentos' },
];

export function TransactionFormRedesigned({
  initialData,
  companyId,
  selectedCompanies = [],
  onSubmit,
  onCancel,
  isSubmitting = false,
  isMobile = false,
  forceRecurring = false,
}: TransactionFormRedesignedProps) {
  const isEditing = !!initialData?.id;
  const [currentStep, setCurrentStep] = useState<FormStep>('basics');
  const [completedSteps, setCompletedSteps] = useState<Set<FormStep>>(new Set());
  
  // Buscar opções do banco
  const options = useTransactionOptions(companyId);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm<TransactionCreateData>({
    resolver: zodResolver(transactionCreateSchema),
    mode: 'onBlur',
    defaultValues: {
      entry_type: initialData?.entry_type || 'expense',
      amount: initialData?.amount ? Number(initialData.amount) : undefined,
      currency: initialData?.currency || 'BRL',
      description: initialData?.description || '',
      notes: initialData?.notes || undefined,
      
      main_category: initialData?.main_category || '',
      subcategory: initialData?.subcategory || undefined,
      classification: initialData?.classification || 'Eventual',
      budget_category: initialData?.budget_category || 'OPEX (Despesas Operacionais)',
      cost_center: initialData?.cost_center || undefined,
      project_id: initialData?.project_id || undefined,
      
      entry_date: initialData?.entry_date || new Date().toISOString().split('T')[0],
      due_date: initialData?.due_date || undefined,
      payment_date: initialData?.payment_date || undefined,
      
      is_recurring: forceRecurring || initialData?.is_recurring || false,
      frequency: initialData?.frequency || undefined,
      recurrence_end_date: initialData?.recurrence_end_date || undefined,
      
      payment_status: initialData?.payment_status || 'previsão',
      payment_method: initialData?.payment_method || undefined,
      account_name: initialData?.account_name || undefined,
      payment_details: initialData?.payment_details || undefined,
      
      invoice_number: initialData?.invoice_number || undefined,
      tax_category: initialData?.tax_category || 'Dedutível',
      business_partner: initialData?.business_partner || undefined,
      location: initialData?.location || undefined,
      stakeholders: initialData?.stakeholders || undefined,
      document_source: initialData?.document_source || undefined,
      tags: initialData?.tags || undefined,
      
      is_simulation: initialData?.is_simulation || false,
    },
  });

  // Watch form values
  const entryType = watch('entry_type');
  const mainCategory = watch('main_category');
  const isRecurring = watch('is_recurring');
  const paymentStatus = watch('payment_status');
  const dueDate = watch('due_date');

  // ✅ Auto-fill entry_date with due_date when recurring is enabled OR when due_date changes
  useEffect(() => {
    if (isRecurring && dueDate) {
      // Se recorrência ativa e há due_date, sempre sincronizar entry_date
      setValue('entry_date', dueDate);
    }
  }, [isRecurring, dueDate, setValue]);

  // Prepare combobox options from database
  const mainCategoryOptions: ComboboxOption[] = (options.categories.mainCategories || []).map(cat => ({
    value: cat,
    label: cat,
  }));

  const categoryOptions: ComboboxOption[] = mainCategory && options.categories.categoriesByMain?.[mainCategory]
    ? Array.from(options.categories.categoriesByMain[mainCategory].categories).map(cat => ({
        value: cat,
        label: cat,
      }))
    : [];

  const subcategoryOptions: ComboboxOption[] = mainCategory && options.categories.categoriesByMain?.[mainCategory]
    ? Array.from(options.categories.categoriesByMain[mainCategory].subcategories).map(cat => ({
        value: cat,
        label: cat,
      }))
    : [];

  const partnerOptions: ComboboxOption[] = (options.businessPartners || []).map((p: { name: string }) => ({
    value: p.name,
    label: p.name,
  }));

  const sourceOptions: ComboboxOption[] = (options.documentSources || []).map((s: { name: string }) => ({
    value: s.name,
    label: s.name,
  }));

  const paymentMethodOptions: ComboboxOption[] = (options.paymentMethods || []).map((m: { name: string }) => ({
    value: m.name,
    label: m.name,
  }));

  const accountOptions: ComboboxOption[] = (options.accounts || []).map((a: { account_name: string }) => ({
    value: a.account_name,
    label: a.account_name,
  }));

  const costCenterOptions: ComboboxOption[] = (options.costCenters || []).map((c: any) => ({
    value: c.name,
    label: c.name,
  }));

  const stakeholderOptions: ComboboxOption[] = (options.stakeholders || []).map((s: any) => ({
    value: s.name,
    label: s.name,
  }));

  const locationOptions: ComboboxOption[] = (options.locations || []).map((l: any) => ({
    value: l.name,
    label: l.name,
  }));

  const tagOptions: ComboboxOption[] = (options.tags || []).map((t: any) => ({
    value: t.name,
    label: t.name,
  }));

  const projectOptions: ComboboxOption[] = (options.projects || []).map((p: any) => ({
    value: p.id,
    label: p.name,
  }));

  const handleFormSubmit = async (data: TransactionCreateData) => {
    try {
      // Debug: Verificar dados de recorrência sendo enviados
      console.log('[TransactionForm] Submitting data:', {
        is_recurring: data.is_recurring,
        frequency: data.frequency,
        recurrence_end_date: data.recurrence_end_date,
        full_data: data
      });

      await onSubmit({
        ...data,
        company_id: companyId,
      });
    } catch (error) {
      notify.error('Erro ao salvar', error instanceof Error ? error.message : 'Tente novamente');
    }
  };

  // Step navigation for mobile
  const goToNextStep = async () => {
    let isValid = false;
    
    switch (currentStep) {
      case 'basics':
        isValid = await trigger(['entry_type', 'amount', 'description', 'entry_date']);
        if (isValid) {
          setCompletedSteps(prev => new Set([...prev, 'basics']));
          setCurrentStep('categorization');
        }
        break;
      case 'categorization':
        isValid = await trigger(['main_category', 'classification', 'budget_category']);
        if (isValid) {
          setCompletedSteps(prev => new Set([...prev, 'categorization']));
          setCurrentStep('payment');
        }
        break;
      case 'payment':
        isValid = await trigger(['payment_status', 'payment_method', 'account_name']);
        if (isValid) {
          setCompletedSteps(prev => new Set([...prev, 'payment']));
          setCurrentStep('additional');
        }
        break;
      case 'additional':
        setCompletedSteps(prev => new Set([...prev, 'additional']));
        setCurrentStep('recurrence');
        break;
      case 'recurrence':
        if (isRecurring) {
          isValid = await trigger(['frequency']);
          if (!isValid) return;
        }
        setCompletedSteps(prev => new Set([...prev, 'recurrence']));
        handleSubmit(handleFormSubmit)();
        break;
    }
  };

  const goToPreviousStep = () => {
    const steps: FormStep[] = ['basics', 'categorization', 'payment', 'additional', 'recurrence'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  // Mobile: Step-by-step wizard
  if (isMobile) {
    return (
      <form className="h-full flex flex-col bg-background" onSubmit={handleSubmit(handleFormSubmit)}>
        {/* Progress Bar */}
        <div className="flex-none bg-background border-b">
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-muted-foreground">
                Passo {['basics', 'categorization', 'payment', 'additional', 'recurrence'].indexOf(currentStep) + 1} de 5
              </span>
              <span className="text-sm font-semibold">
                {currentStep === 'basics' && 'Informações Básicas'}
                {currentStep === 'categorization' && 'Categorização'}
                {currentStep === 'payment' && 'Pagamento'}
                {currentStep === 'additional' && 'Adicional'}
                {currentStep === 'recurrence' && 'Recorrência'}
              </span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={{ width: '20%' }}
                animate={{
                  width: currentStep === 'basics' ? '20%' :
                         currentStep === 'categorization' ? '40%' :
                         currentStep === 'payment' ? '60%' :
                         currentStep === 'additional' ? '80%' : '100%'
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              />
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="p-4 pb-6 space-y-4 min-h-full">
            <AnimatePresence mode="wait">
              {currentStep === 'basics' && (
                <motion.div
                  key="basics"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <BasicsStepMobile
                    control={control}
                    register={register}
                    errors={errors}
                    entryType={entryType}
                    selectedCompanies={selectedCompanies}
                    companyId={companyId}
                  />
                </motion.div>
              )}

              {currentStep === 'categorization' && (
                <motion.div
                  key="categorization"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <CategorizationStepMobile
                    control={control}
                    errors={errors}
                    options={{
                      mainCategories: mainCategoryOptions,
                      subcategories: subcategoryOptions,
                      costCenters: costCenterOptions,
                      projects: projectOptions,
                    }}
                    isLoading={options.isLoading}
                  />
                </motion.div>
              )}

              {currentStep === 'payment' && (
                <motion.div
                  key="payment"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <PaymentStepMobile
                    control={control}
                    register={register}
                    errors={errors}
                    options={{
                      paymentMethods: paymentMethodOptions,
                      accounts: accountOptions,
                    }}
                    isLoading={options.isLoading}
                    paymentStatus={paymentStatus}
                  />
                </motion.div>
              )}

              {currentStep === 'additional' && (
                <motion.div
                  key="additional"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <AdditionalStepMobile
                    control={control}
                    register={register}
                    errors={errors}
                    options={{
                      partners: partnerOptions,
                      sources: sourceOptions,
                      locations: locationOptions,
                    }}
                    isLoading={options.isLoading}
                  />
                </motion.div>
              )}

              {currentStep === 'recurrence' && (
                <motion.div
                  key="recurrence"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <RecurrenceStepMobile
                    control={control}
                    register={register}
                    errors={errors}
                    isRecurring={isRecurring}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Fixed Bottom Actions */}
        <div className="flex-none border-t bg-background/95 backdrop-blur-sm">
          <div className="p-4 space-y-3 safe-bottom">
            {errors && Object.keys(errors).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start gap-2"
              >
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-xs text-destructive">
                  <p className="font-medium mb-1">Corrija os erros:</p>
                  <ul className="space-y-0.5">
                    {Object.entries(errors).slice(0, 3).map(([field, error]) => (
                      <li key={field}>• {error?.message as string}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}

            <div className="flex gap-3">
              {currentStep !== 'basics' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToPreviousStep}
                  className="flex-1 h-12 text-base font-medium focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  disabled={isSubmitting}
                  aria-label="Voltar para etapa anterior"
                >
                  Voltar
                </Button>
              )}
              
              <Button
                type="button"
                onClick={goToNextStep}
                className={cn(
                  "h-12 text-base font-medium gap-2 transition-all",
                  currentStep === 'basics' ? "flex-[2]" : "flex-1",
                  currentStep === 'recurrence' && "bg-green-600 hover:bg-green-700"
                )}
                disabled={isSubmitting || options.isLoading}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : currentStep === 'recurrence' ? (
                  <>
                    <CheckCircle2 className="h-5 w-5" />
                    Concluir
                  </>
                ) : (
                  <>
                    Próximo
                    <ChevronRight className="h-5 w-5" />
                  </>
                )}
              </Button>
            </div>

            {options.isLoading && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs text-center text-muted-foreground"
              >
                Carregando opções...
              </motion.p>
            )}
          </div>
        </div>
      </form>
    );
  }

  // Desktop: Normal form with natural scroll (like other pages)
  return (
    <Card className="shadow-sm">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-8 p-6 md:p-8">
        {/* Basics Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="p-2.5 bg-primary/10 rounded-lg">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Informações Básicas</h3>
              {options.isLoading && (
                <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Carregando...
                </span>
              )}
            </div>
          </div>

          {/* Entry Type */}
          <div className="space-y-2">
            <Label>Tipo de Lançamento *</Label>
            <Controller
              name="entry_type"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="flex gap-2"
                >
                  <label
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all',
                      field.value === 'income'
                        ? 'border-green-500 bg-green-50 text-green-900'
                        : 'border-muted hover:border-muted-foreground/50'
                    )}
                  >
                    <RadioGroupItem value="income" className="sr-only" />
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm font-medium">Receita</span>
                  </label>

                  <label
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all',
                      field.value === 'expense'
                        ? 'border-red-500 bg-red-50 text-red-900'
                        : 'border-muted hover:border-muted-foreground/50'
                    )}
                  >
                    <RadioGroupItem value="expense" className="sr-only" />
                    <TrendingDown className="h-4 w-4" />
                    <span className="text-sm font-medium">Despesa</span>
                  </label>

                  <label
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all',
                      field.value === 'transfer'
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-muted hover:border-muted-foreground/50'
                    )}
                  >
                    <RadioGroupItem value="transfer" className="sr-only" />
                    <ArrowLeftRight className="h-4 w-4" />
                    <span className="text-sm font-medium">Transferência</span>
                  </label>
                </RadioGroup>
              )}
            />
            {errors.entry_type && (
              <p className="text-xs text-destructive">{errors.entry_type.message}</p>
            )}
          </div>

          {/* Amount, Currency & Date */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Valor *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="0,00"
                {...register('amount', { valueAsNumber: true })}
                className={cn('h-11 text-lg focus-visible:ring-2 focus-visible:ring-primary', errors.amount && 'border-destructive')}
                aria-label="Valor do lançamento"
                aria-required="true"
                aria-invalid={!!errors.amount}
                aria-describedby={errors.amount ? 'amount-error' : undefined}
              />
              {errors.amount && (
                <p id="amount-error" className="text-xs text-destructive" role="alert">{errors.amount.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">Moeda</Label>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <CurrencySelect
                    value={field.value}
                    onChange={field.onChange}
                    className="h-11"
                  />
                )}
              />
              {errors.currency && (
                <p className="text-xs text-destructive">{errors.currency.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="entry_date">Data *</Label>
              <Input
                id="entry_date"
                type="date"
                {...register('entry_date')}
                className={cn('h-11 focus-visible:ring-2 focus-visible:ring-primary', errors.entry_date && 'border-destructive')}
                aria-label="Data do lançamento"
                aria-required="true"
                aria-invalid={!!errors.entry_date}
                aria-describedby={errors.entry_date ? 'entry-date-error' : undefined}
              />
              {errors.entry_date && (
                <p id="entry-date-error" className="text-xs text-destructive" role="alert">{errors.entry_date.message}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição *</Label>
            <Input
              id="description"
              placeholder="Ex: Pagamento de fornecedor"
              {...register('description')}
              className={cn('h-11 focus-visible:ring-2 focus-visible:ring-primary', errors.description && 'border-destructive')}
              aria-label="Descrição do lançamento"
              aria-required="true"
              aria-invalid={!!errors.description}
              aria-describedby={errors.description ? 'description-error' : undefined}
            />
            {errors.description && (
              <p id="description-error" className="text-xs text-destructive" role="alert">{errors.description.message}</p>
            )}
          </div>

          {/* Date of Due */}
          <div className="space-y-2">
            <Label htmlFor="due_date">Data de Vencimento</Label>
            <Input
              id="due_date"
              type="date"
              {...register('due_date')}
              className={cn(errors.due_date && 'border-destructive')}
            />
            {errors.due_date && (
              <p className="text-xs text-destructive">{errors.due_date.message}</p>
            )}
          </div>
        </section>

        {/* Categorization Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="p-2.5 bg-primary/10 rounded-lg">
              <Tag className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold">Categorização</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Main Category */}
            <div className="space-y-2">
              <Label htmlFor="main_category">Categoria Principal *</Label>
              <Controller
                name="main_category"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={mainCategoryOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Selecione ou crie"
                    searchPlaceholder="Buscar..."
                    className={cn(errors.main_category && 'border-destructive')}
                    allowCustom
                    onCreate={async (name) => {
                      // Criar categoria principal como receita por padrão, pode ser ajustado
                      await options.createCategory({ name, entryType: entryType || 'expense' });
                    }}
                  />
                )}
              />
              {errors.main_category && (
                <p className="text-xs text-destructive">{errors.main_category.message}</p>
              )}
            </div>

            {/* Subcategory */}
            <div className="space-y-2">
              <Label htmlFor="subcategory">Subcategoria</Label>
              <Controller
                name="subcategory"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={subcategoryOptions}
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    placeholder="Selecione ou crie"
                    searchPlaceholder="Buscar..."
                    allowCustom
                    disabled={!mainCategory}
                    onCreate={async (name) => {
                      if (mainCategory) {
                        await options.createSubcategory({ name, mainCategory });
                      }
                    }}
                  />
                )}
              />
            </div>

            {/* Classification */}
            <div className="space-y-2">
              <Label htmlFor="classification">Classificação</Label>
              <Controller
                name="classification"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={CLASSIFICATION_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Selecione"
                  />
                )}
              />
            </div>

            {/* Budget Category */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="budget_category">Categoria DFC (Budget)</Label>
              <Controller
                name="budget_category"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={BUDGET_CATEGORY_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Selecione"
                    searchPlaceholder="Buscar categoria..."
                  />
                )}
              />
            </div>

            {/* Cost Center */}
            <div className="space-y-2">
              <Label htmlFor="cost_center">Centro de Custo</Label>
              <Controller
                name="cost_center"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={costCenterOptions}
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    placeholder="Selecione ou crie"
                    searchPlaceholder="Buscar..."
                    allowCustom
                    onCreate={(name) => options.createCostCenter({ name })}
                  />
                )}
              />
            </div>
          </div>

          {/* Project - Full width */}
          <div className="space-y-2">
            <Label htmlFor="project_id">Projeto</Label>
            <Controller
              name="project_id"
              control={control}
              render={({ field }) => (
                <Combobox
                  options={projectOptions}
                  value={field.value || ''}
                  onValueChange={field.onChange}
                  placeholder="Selecione um projeto"
                  searchPlaceholder="Buscar projeto..."
                />
              )}
            />
            <p className="text-xs text-muted-foreground">
              Vincule este lançamento a um projeto cadastrado
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
          </div>
        </section>

        {/* Payment Info Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="p-2.5 bg-primary/10 rounded-lg">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold">Informações de Pagamento</h3>
          </div>

          <div className="space-y-4">
            {/* Payment Status - Full width */}
            <div className="space-y-2">
              <Label htmlFor="payment_status">Status *</Label>
              <Controller
                name="payment_status"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="grid grid-cols-2 gap-2"
                  >
                    <label
                      className={cn(
                        'flex items-center justify-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all',
                        field.value === 'confirmado'
                          ? 'border-primary bg-primary/5'
                          : 'border-muted hover:border-muted-foreground/50'
                      )}
                    >
                      <RadioGroupItem value="confirmado" className="sr-only" />
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="text-sm">Confirmado</span>
                    </label>

                    <label
                      className={cn(
                        'flex items-center justify-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all',
                        field.value === 'previsão'
                          ? 'border-primary bg-primary/5'
                          : 'border-muted hover:border-muted-foreground/50'
                      )}
                    >
                      <RadioGroupItem value="previsão" className="sr-only" />
                      <Calendar className="h-4 w-4" />
                      <span className="text-sm">Previsão</span>
                    </label>
                  </RadioGroup>
                )}
              />
            </div>

            {/* Payment Date - Only show when status is "confirmado" with smooth animation */}
            <AnimatePresence mode="wait">
              {paymentStatus === 'confirmado' && (
                <motion.div 
                  className="space-y-2"
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginTop: 0 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <Label htmlFor="payment_date">Data de Pagamento *</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    {...register('payment_date')}
                    className={cn(errors.payment_date && 'border-destructive')}
                  />
                  {errors.payment_date && (
                    <p className="text-xs text-destructive">{errors.payment_date.message}</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Payment Method and Account - Grid of 2 */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payment_method">Método de Pagamento</Label>
                <Controller
                  name="payment_method"
                  control={control}
                  render={({ field }) => (
                    <Combobox
                      options={paymentMethodOptions}
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Selecione ou crie"
                      searchPlaceholder="Buscar..."
                      allowCustom
                      onCreate={(name) => options.createPaymentMethod({ name })}
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="account_name">Conta Bancária</Label>
                <Controller
                  name="account_name"
                  control={control}
                  render={({ field }) => (
                    <Combobox
                      options={accountOptions}
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Selecione ou crie"
                      searchPlaceholder="Buscar..."
                      allowCustom
                      onCreate={(name) => options.createAccount({ name })}
                    />
                  )}
                />
              </div>
            </div>

            {/* Payment Details - Full width */}
            <div className="space-y-2">
              <Label htmlFor="payment_details">Detalhes do Pagamento</Label>
              <Input
                id="payment_details"
                placeholder="Ex: Parcela 1/12"
                {...register('payment_details')}
              />
            </div>
          </div>
        </section>

        {/* Additional Info Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="p-2.5 bg-primary/10 rounded-lg">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold">Informações Adicionais</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Business Partner */}
            <div className="space-y-2">
              <Label htmlFor="business_partner">Parceiro Comercial</Label>
              <Controller
                name="business_partner"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={partnerOptions}
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    placeholder="Selecione ou crie"
                    searchPlaceholder="Buscar..."
                    allowCustom
                    onCreate={(name) => options.createBusinessPartner({ name })}
                  />
                )}
              />
            </div>

            {/* Document Source */}
            <div className="space-y-2">
              <Label htmlFor="document_source">Fonte do Documento</Label>
              <Controller
                name="document_source"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={sourceOptions}
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    placeholder="Selecione ou crie"
                    searchPlaceholder="Buscar..."
                    allowCustom
                    onCreate={(name) => options.createDocumentSource({ name })}
                  />
                )}
              />
            </div>

            {/* Invoice Number */}
            <div className="space-y-2">
              <Label htmlFor="invoice_number">Número da NF</Label>
              <Input
                id="invoice_number"
                placeholder="Ex: NF-12345"
                {...register('invoice_number')}
              />
            </div>

            {/* Tax Category */}
            <div className="space-y-2">
              <Label htmlFor="tax_category">Categoria Tributária</Label>
              <Controller
                name="tax_category"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={TAX_CATEGORY_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Selecione"
                  />
                )}
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location">Localização</Label>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={locationOptions}
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    placeholder="Selecione ou crie"
                    searchPlaceholder="Buscar..."
                    allowCustom
                    onCreate={(name) => options.createLocation({ name })}
                  />
                )}
              />
            </div>

            {/* Stakeholders */}
            <div className="space-y-2">
              <Label htmlFor="stakeholders">Stakeholders</Label>
              <MultiSelectCombobox
                options={stakeholderOptions}
                value={watch('stakeholders') || ''}
                onValueChange={(value) => setValue('stakeholders', value)}
                placeholder="Selecione ou adicione stakeholders"
                searchPlaceholder="Buscar ou criar..."
                onCreate={(name) => options.createStakeholder({ name })}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                placeholder="Observações adicionais..."
                rows={3}
                {...register('notes')}
                className={cn(errors.notes && 'border-destructive')}
              />
              {errors.notes && (
                <p className="text-xs text-destructive">{errors.notes.message}</p>
              )}
            </div>
          </div>
        </section>

        {/* Recurrence Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="p-2.5 bg-primary/10 rounded-lg">
              <Repeat className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Recorrência</h3>
              <p className="text-sm text-muted-foreground mt-0.5">Configure lançamentos automáticos</p>
            </div>
          </div>

          {/* Is Recurring Toggle */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="is_recurring" className="text-base cursor-pointer">
                Lançamento Recorrente
              </Label>
              <p className="text-sm text-muted-foreground">
                Criar lançamentos automáticos periodicamente
              </p>
            </div>
            <Controller
              name="is_recurring"
              control={control}
              render={({ field }) => (
                <Switch
                  id="is_recurring"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Recurrence Fields (shown when enabled) */}
          <AnimatePresence>
            {isRecurring && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="space-y-4 overflow-hidden"
              >
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Start Date (mirror of entry_date) */}
                  <div className="space-y-2">
                    <Label htmlFor="entry_date_recurrence">Data Inicial *</Label>
                    <Input
                      id="entry_date_recurrence"
                      type="date"
                      {...register('entry_date')}
                      className={cn(errors.entry_date && 'border-destructive')}
                    />
                    {errors.entry_date && (
                      <p className="text-xs text-destructive">{errors.entry_date.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Primeiro lançamento será criado nesta data
                    </p>
                  </div>

                  {/* Frequency */}
                  <div className="space-y-2">
                    <Label htmlFor="frequency">Frequência *</Label>
                    <Controller
                      name="frequency"
                      control={control}
                      render={({ field }) => (
                        <Combobox
                          options={FREQUENCY_OPTIONS}
                          value={field.value || ''}
                          onValueChange={field.onChange}
                          placeholder="Selecione"
                          className={cn(errors.frequency && 'border-destructive')}
                        />
                      )}
                    />
                    {errors.frequency && (
                      <p className="text-xs text-destructive">{errors.frequency.message}</p>
                    )}
                  </div>

                  {/* End Date */}
                  <div className="space-y-2">
                    <Label htmlFor="recurrence_end_date">Data Final</Label>
                    <Input
                      id="recurrence_end_date"
                      type="date"
                      {...register('recurrence_end_date')}
                      className={cn(errors.recurrence_end_date && 'border-destructive')}
                    />
                    {errors.recurrence_end_date && (
                      <p className="text-xs text-destructive">{errors.recurrence_end_date.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Último lançamento será criado nesta data
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {/* Error Summary - If there are errors */}
        {Object.keys(errors).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-destructive/10 border-2 border-destructive/30 rounded-xl p-5 flex items-start gap-3"
          >
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-destructive mb-2.5">
                Corrija os seguintes erros para continuar:
              </p>
              <ul className="text-sm text-destructive/90 space-y-1.5 max-h-32 overflow-y-auto">
                {Object.entries(errors).map(([field, error]) => (
                  <li key={field} className="flex gap-2">
                    <span>•</span>
                    <span>{error?.message as string}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}

        {/* Form Actions - Bottom buttons */}
        <div className="flex gap-4 justify-end pt-6 border-t">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
              className="gap-2 h-11 px-8 text-base focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label="Cancelar e voltar"
            >
              <X className="h-4 w-4" />
              Cancelar
            </Button>
          )}

          <Button
            type="submit"
            disabled={isSubmitting || options.isLoading}
            className="gap-2 min-w-[180px] h-11 px-8 text-base font-semibold shadow-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            aria-label={isEditing ? 'Atualizar lançamento' : 'Salvar novo lançamento'}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {isEditing ? 'Atualizar Lançamento' : 'Salvar Lançamento'}
              </>
            )}
          </Button>
        </div>

        {options.isLoading && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-center text-muted-foreground flex items-center justify-center gap-2 -mt-2"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando opções do banco de dados...
          </motion.p>
        )}
      </form>
    </Card>
  );
}

// Mobile Step Components
function BasicsStepMobile({ control, register, errors, entryType, selectedCompanies, companyId }: any) {
  const showCompanySelector = selectedCompanies && selectedCompanies.length > 1;
  
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold mb-1">Informações Básicas</h2>
        <p className="text-sm text-muted-foreground">Dados principais do lançamento</p>
      </div>

      {/* Company Selector - Only if multiple companies selected */}
      {showCompanySelector && (
        <div className="space-y-2">
          <Label htmlFor="company_id">Empresa *</Label>
          <Controller
            name="company_id"
            control={control}
            render={({ field }) => (
              <Select value={field.value || companyId} onValueChange={field.onChange}>
                <SelectTrigger className={cn('h-12', errors.company_id && 'border-destructive')}>
                  <SelectValue placeholder="Selecione a empresa" />
                </SelectTrigger>
                <SelectContent>
                  {selectedCompanies.map((company: any) => (
                    <SelectItem key={company.id} value={company.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        {company.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.company_id && (
            <p className="text-xs text-destructive">{errors.company_id.message}</p>
          )}
        </div>
      )}

      {/* Entry Type */}
      <div className="space-y-2">
        <Label>Tipo *</Label>
        <Controller
          name="entry_type"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="grid grid-cols-3 gap-2"
            >
              <label
                className={cn(
                  'flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all',
                  field.value === 'income'
                    ? 'border-green-500 bg-green-50'
                    : 'border-muted'
                )}
              >
                <RadioGroupItem value="income" className="sr-only" />
                <TrendingUp className={cn('h-6 w-6', field.value === 'income' ? 'text-green-600' : 'text-muted-foreground')} />
                <span className="text-xs font-medium text-center">Receita</span>
              </label>

              <label
                className={cn(
                  'flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all',
                  field.value === 'expense'
                    ? 'border-red-500 bg-red-50'
                    : 'border-muted'
                )}
              >
                <RadioGroupItem value="expense" className="sr-only" />
                <TrendingDown className={cn('h-6 w-6', field.value === 'expense' ? 'text-red-600' : 'text-muted-foreground')} />
                <span className="text-xs font-medium text-center">Despesa</span>
              </label>

              <label
                className={cn(
                  'flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all',
                  field.value === 'transfer'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-muted'
                )}
              >
                <RadioGroupItem value="transfer" className="sr-only" />
                <ArrowLeftRight className={cn('h-6 w-6', field.value === 'transfer' ? 'text-blue-600' : 'text-muted-foreground')} />
                <span className="text-xs font-medium text-center">Transfer.</span>
              </label>
            </RadioGroup>
          )}
        />
        {errors.entry_type && (
          <p className="text-xs text-destructive">{errors.entry_type.message}</p>
        )}
      </div>

      {/* Amount */}
      <div className="space-y-2">
        <Label htmlFor="amount">Valor *</Label>
        <Input
          id="amount"
          type="number"
          step="0.01"
          placeholder="0,00"
          {...register('amount', { valueAsNumber: true })}
          className={cn('h-12 text-lg', errors.amount && 'border-destructive')}
          autoFocus
        />
        {errors.amount && (
          <p className="text-xs text-destructive">{errors.amount.message}</p>
        )}
      </div>

      {/* Currency */}
      <div className="space-y-2">
        <Label htmlFor="currency">Moeda</Label>
        <Controller
          name="currency"
          control={control}
          render={({ field }) => (
            <CurrencySelect
              value={field.value}
              onChange={field.onChange}
              className="h-12"
            />
          )}
        />
        {errors.currency && (
          <p className="text-xs text-destructive">{errors.currency.message}</p>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Descrição *</Label>
        <Input
          id="description"
          placeholder="Ex: Pagamento fornecedor"
          {...register('description')}
          className={cn('h-12', errors.description && 'border-destructive')}
        />
        {errors.description && (
          <p className="text-xs text-destructive">{errors.description.message}</p>
        )}
      </div>

      {/* Entry Date */}
      <div className="space-y-2">
        <Label htmlFor="entry_date">Data do Lançamento *</Label>
        <Input
          id="entry_date"
          type="date"
          {...register('entry_date')}
          className={cn('h-12', errors.entry_date && 'border-destructive')}
        />
        {errors.entry_date && (
          <p className="text-xs text-destructive">{errors.entry_date.message}</p>
        )}
      </div>
    </div>
  );
}

function CategorizationStepMobile({ control, errors, options, isLoading }: any) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold mb-1">Categorização</h2>
        <p className="text-sm text-muted-foreground">Organize e classifique o lançamento</p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando dados...
        </div>
      )}

      {/* Main Category */}
      <div className="space-y-2">
        <Label>Categoria Principal *</Label>
        <Controller
          name="main_category"
          control={control}
          render={({ field }) => (
            <Combobox
              options={options.mainCategories}
              value={field.value}
              onValueChange={field.onChange}
              placeholder="Selecione ou crie"
              searchPlaceholder="Buscar..."
              className={cn('h-12', errors.main_category && 'border-destructive')}
              allowCustom
              onCreate={async (name) => {
                // Criar categoria principal
                await options.createCategory({ name, entryType: 'expense' });
              }}
            />
          )}
        />
        {errors.main_category && (
          <p className="text-xs text-destructive">{errors.main_category.message}</p>
        )}
      </div>

      {/* Subcategory */}
      <div className="space-y-2">
        <Label>Subcategoria</Label>
        <Controller
          name="subcategory"
          control={control}
          render={({ field }) => (
            <Combobox
              options={options.subcategories}
              value={field.value || ''}
              onValueChange={field.onChange}
              placeholder="Selecione ou crie"
              searchPlaceholder="Buscar..."
              className="h-12"
              allowCustom
              onCreate={async (name) => {
                const mainCategory = watch('main_category');
                if (mainCategory) {
                  await options.createSubcategory({ name, mainCategory });
                }
              }}
            />
          )}
        />
      </div>

      {/* Classification */}
      <div className="space-y-2">
        <Label>Classificação</Label>
        <Controller
          name="classification"
          control={control}
          render={({ field }) => (
            <Combobox
              options={CLASSIFICATION_OPTIONS}
              value={field.value}
              onValueChange={field.onChange}
              placeholder="Selecione"
              className="h-12"
            />
          )}
        />
      </div>

      {/* Budget Category */}
      <div className="space-y-2">
        <Label>Categoria DFC</Label>
        <Controller
          name="budget_category"
          control={control}
          render={({ field }) => (
            <Combobox
              options={BUDGET_CATEGORY_OPTIONS}
              value={field.value}
              onValueChange={field.onChange}
              placeholder="Selecione"
              searchPlaceholder="Buscar..."
              className="h-12"
            />
          )}
        />
      </div>

      {/* Cost Center */}
      <div className="space-y-2">
        <Label>Centro de Custo</Label>
        <Controller
          name="cost_center"
          control={control}
          render={({ field }) => (
            <Combobox
              options={options.costCenters}
              value={field.value || ''}
              onValueChange={field.onChange}
              placeholder="Selecione ou crie"
              searchPlaceholder="Buscar..."
              className="h-12"
              allowCustom
            />
          )}
        />
      </div>

      {/* Project */}
      <div className="space-y-2">
        <Label>Projeto</Label>
        <Controller
          name="project_id"
          control={control}
          render={({ field }) => (
            <Combobox
              options={options.projects}
              value={field.value || ''}
              onValueChange={field.onChange}
              placeholder="Selecione um projeto"
              searchPlaceholder="Buscar projeto..."
              className="h-12"
            />
          )}
        />
        <p className="text-xs text-muted-foreground">
          Vincule este lançamento a um projeto
        </p>
      </div>
    </div>
  );
}

function PaymentStepMobile({ control, register, errors, options, isLoading, paymentStatus }: any) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold mb-1">Pagamento</h2>
        <p className="text-sm text-muted-foreground">Informações de pagamento</p>
      </div>

      {/* Payment Status */}
      <div className="space-y-2">
        <Label>Status *</Label>
        <Controller
          name="payment_status"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="grid grid-cols-2 gap-3"
            >
              <label
                className={cn(
                  'flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all',
                  field.value === 'confirmado'
                    ? 'border-primary bg-primary/5'
                    : 'border-muted'
                )}
              >
                <RadioGroupItem value="confirmado" className="sr-only" />
                <CheckCircle2 className={cn('h-5 w-5', field.value === 'confirmado' ? 'text-primary' : 'text-muted-foreground')} />
                <div className="flex-1">
                  <div className="text-sm font-medium">Confirmado</div>
                  <div className="text-xs text-muted-foreground">Pago</div>
                </div>
              </label>

              <label
                className={cn(
                  'flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all',
                  field.value === 'previsão'
                    ? 'border-primary bg-primary/5'
                    : 'border-muted'
                )}
              >
                <RadioGroupItem value="previsão" className="sr-only" />
                <Calendar className={cn('h-5 w-5', field.value === 'previsão' ? 'text-primary' : 'text-muted-foreground')} />
                <div className="flex-1">
                  <div className="text-sm font-medium">Previsão</div>
                  <div className="text-xs text-muted-foreground">A pagar</div>
                </div>
              </label>
            </RadioGroup>
          )}
        />
      </div>

      {/* Due Date & Payment Date */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="due_date" className="text-xs">Vencimento</Label>
          <Input
            id="due_date"
            type="date"
            {...register('due_date')}
            className={cn('h-11', errors.due_date && 'border-destructive')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="payment_date" className="text-xs">Pagamento</Label>
          <Input
            id="payment_date"
            type="date"
            {...register('payment_date')}
            className={cn('h-11', errors.payment_date && 'border-destructive')}
          />
        </div>
      </div>

      {/* Payment Method */}
      <div className="space-y-2">
        <Label>Método de Pagamento</Label>
        <Controller
          name="payment_method"
          control={control}
          render={({ field }) => (
            <Combobox
              options={options.paymentMethods}
              value={field.value || ''}
              onValueChange={field.onChange}
              placeholder="Selecione ou crie"
              searchPlaceholder="Buscar..."
              className="h-12"
              allowCustom
            />
          )}
        />
      </div>

      {/* Account */}
      <div className="space-y-2">
        <Label>Conta Bancária</Label>
        <Controller
          name="account_name"
          control={control}
          render={({ field }) => (
            <Combobox
              options={options.accounts}
              value={field.value || ''}
              onValueChange={field.onChange}
              placeholder="Selecione ou crie"
              searchPlaceholder="Buscar..."
              className="h-12"
              allowCustom
            />
          )}
        />
      </div>

      {/* Payment Details */}
      <div className="space-y-2">
        <Label htmlFor="payment_details">Detalhes</Label>
        <Input
          id="payment_details"
          placeholder="Ex: Parcela 1/12"
          {...register('payment_details')}
          className="h-12"
        />
      </div>
    </div>
  );
}

function AdditionalStepMobile({ control, register, errors, options, isLoading }: any) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold mb-1">Informações Adicionais</h2>
        <p className="text-sm text-muted-foreground">Dados complementares opcionais</p>
      </div>

      {/* Business Partner */}
      <div className="space-y-2">
        <Label>Parceiro Comercial</Label>
        <Controller
          name="business_partner"
          control={control}
          render={({ field }) => (
            <Combobox
              options={options.partners}
              value={field.value || ''}
              onValueChange={field.onChange}
              placeholder="Selecione ou crie"
              searchPlaceholder="Buscar..."
              className="h-12"
              allowCustom
            />
          )}
        />
      </div>

      {/* Document Source */}
      <div className="space-y-2">
        <Label>Fonte do Documento</Label>
        <Controller
          name="document_source"
          control={control}
          render={({ field }) => (
            <Combobox
              options={options.sources}
              value={field.value || ''}
              onValueChange={field.onChange}
              placeholder="Selecione ou crie"
              searchPlaceholder="Buscar..."
              className="h-12"
              allowCustom
            />
          )}
        />
      </div>

      {/* Invoice Number */}
      <div className="space-y-2">
        <Label htmlFor="invoice_number">Número da NF</Label>
        <Input
          id="invoice_number"
          placeholder="Ex: NF-12345"
          {...register('invoice_number')}
          className="h-12"
        />
      </div>

      {/* Tax Category */}
      <div className="space-y-2">
        <Label>Categoria Tributária</Label>
        <Controller
          name="tax_category"
          control={control}
          render={({ field }) => (
            <Combobox
              options={TAX_CATEGORY_OPTIONS}
              value={field.value}
              onValueChange={field.onChange}
              placeholder="Selecione"
              className="h-12"
            />
          )}
        />
      </div>

      {/* Location */}
      <div className="space-y-2">
        <Label>Localização</Label>
        <Controller
          name="location"
          control={control}
          render={({ field }) => (
            <Combobox
              options={options.locations}
              value={field.value || ''}
              onValueChange={field.onChange}
              placeholder="Selecione ou crie"
              searchPlaceholder="Buscar..."
              className="h-12"
              allowCustom
            />
          )}
        />
      </div>

      {/* Stakeholders */}
      <div className="space-y-2">
        <Label htmlFor="stakeholders">Stakeholders</Label>
        <MultiSelectCombobox
          options={stakeholderOptions}
          value={watch('stakeholders') || ''}
          onValueChange={(value) => setValue('stakeholders', value)}
          placeholder="Selecione ou adicione stakeholders"
          searchPlaceholder="Buscar ou criar..."
          onCreate={(name) => options.createStakeholder({ name })}
          className="h-12"
        />
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes">Notas</Label>
        <Textarea
          id="notes"
          placeholder="Observações adicionais..."
          rows={4}
          {...register('notes')}
          className={cn(errors.notes && 'border-destructive')}
        />
        {errors.notes && (
          <p className="text-xs text-destructive">{errors.notes.message}</p>
        )}
      </div>
    </div>
  );
}

function RecurrenceStepMobile({ control, register, errors, isRecurring }: any) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold mb-1">Recorrência</h2>
        <p className="text-sm text-muted-foreground">Configure lançamentos automáticos</p>
      </div>

      {/* Is Recurring Toggle */}
      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
        <div className="space-y-0.5">
          <Label htmlFor="is_recurring" className="text-base cursor-pointer">
            Ativar Recorrência
          </Label>
          <p className="text-xs text-muted-foreground">
            Repetir automaticamente
          </p>
        </div>
        <Controller
          name="is_recurring"
          control={control}
          render={({ field }) => (
            <Switch
              id="is_recurring"
              checked={field.value}
              onCheckedChange={field.onChange}
              aria-label="Ativar recorrência automática"
            />
          )}
        />
      </div>

      {/* Recurrence Fields */}
      {isRecurring && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Frequency */}
          <div className="space-y-2">
            <Label>Frequência *</Label>
            <Controller
              name="frequency"
              control={control}
              render={({ field }) => (
                <Combobox
                  options={FREQUENCY_OPTIONS}
                  value={field.value || ''}
                  onValueChange={field.onChange}
                  placeholder="Selecione"
                  className={cn('h-12', errors.frequency && 'border-destructive')}
                />
              )}
            />
            {errors.frequency && (
              <p className="text-xs text-destructive">{errors.frequency.message}</p>
            )}
          </div>

          {/* End Date */}
          <div className="space-y-2">
            <Label htmlFor="recurrence_end_date">Data Final</Label>
            <Input
              id="recurrence_end_date"
              type="date"
              {...register('recurrence_end_date')}
              className={cn('h-12', errors.recurrence_end_date && 'border-destructive')}
            />
            {errors.recurrence_end_date && (
              <p className="text-xs text-destructive">{errors.recurrence_end_date.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Opcional. Deixe em branco para recorrência indefinida
            </p>
          </div>
        </motion.div>
      )}

      {!isRecurring && (
        <div className="p-4 bg-muted/30 rounded-lg text-sm text-muted-foreground text-center">
          Este será um lançamento único
        </div>
      )}
    </div>
  );
}
